import {
  useState,
  useEffect,
  Dispatch,
  SetStateAction,
} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { usePagination } from "./usePagination";
import { useAuthenticatedApi } from "./useAuthenticatedApi";
import {
  HousekeepingType,
  getHousekeepingTypes,
  createHousekeepingType,
  updateHousekeepingType,
  deleteHousekeepingType,
} from "../api/housekeepingApi";

interface UseHousekeepingProps {
  hotelId: string;
  initialPage?: number;
  initialLimit?: number;
  refreshInterval?: number;
}

interface UseHousekeepingReturn {
  items: HousekeepingType[];
  isLoading: boolean;
  error: Error | null;
  selectedItem: HousekeepingType | null;
  setSelectedItem: Dispatch<SetStateAction<HousekeepingType | null>>;
  createItem: (
    item: Omit<HousekeepingType, "id" | "createdAt" | "updatedAt">
  ) => Promise<void>;
  updateItem: (id: string, item: HousekeepingType) => Promise<void>;
  deleteItem: (id: string) => Promise<void>;
  pagination: ReturnType<typeof usePagination>["pagination"];
  setPage: ReturnType<typeof usePagination>["setPage"];
  setLimit: ReturnType<typeof usePagination>["setLimit"];
  refresh: () => Promise<void>;
  setSearchTerm: Dispatch<SetStateAction<string>>;
}

export const useHousekeeping = ({
  hotelId,
  initialPage = 1,
  initialLimit = 10,
  refreshInterval = 30000,
}: UseHousekeepingProps): UseHousekeepingReturn => {
  const { callApi } = useAuthenticatedApi();
  const queryClient = useQueryClient();

  const [searchTerm, setSearchTerm] = useState<string>("");
  const [items, setItems] = useState<HousekeepingType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [selectedItem, setSelectedItem] = useState<HousekeepingType | null>(
    null
  );

  const { pagination, setPage, setLimit, setTotal } = usePagination({
    initialPage,
    initialLimit,
  });

  const {
    data: housekeepingResponse,
    isLoading: queryLoading,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey: [
      "housekeepingTypes",
      hotelId,
      pagination.page,
      pagination.limit,
      searchTerm.trim() || "",
    ],
    enabled: !!hotelId,
    refetchInterval:
      refreshInterval && refreshInterval > 0 ? refreshInterval : false,
    queryFn: async () => {
      try {
        const { data, meta } = await getHousekeepingTypes(
          callApi,
          pagination.page,
          pagination.limit,
          searchTerm
        );

        const total =
          typeof meta.totalCount === "number"
            ? meta.totalCount
            : Array.isArray(data)
            ? data.length
            : 0;

        return {
          items: data,
          total,
        };
      } catch (err) {
        if (err instanceof Error && err.message === "No items found") {
          return {
            items: [],
            total: 0,
          };
        }

        throw err;
      }
    },
  });

  useEffect(() => {
    setIsLoading(queryLoading);
  }, [queryLoading]);

  useEffect(() => {
    setError(queryError instanceof Error ? queryError : null);
  }, [queryError]);

  useEffect(() => {
    if (!housekeepingResponse) return;

    const { items: housekeepingItems, total } = housekeepingResponse;
    setItems(housekeepingItems);
    setTotal(total);
  }, [housekeepingResponse, setTotal]);

  // Reset page to 1 when search term changes
  useEffect(() => {
    setPage(1);
  }, [searchTerm, setPage]);

  const fetchItems = async () => {
    await refetch();
  };

  const createItem = async (
    item: Omit<HousekeepingType, "id" | "createdAt" | "updatedAt">
  ) => {
    setIsLoading(true);
    setError(null);
    try {
      await createHousekeepingType(callApi, {
        type: item.type,
        description: item.description,
        urgency: item.urgency,
        hotelId: hotelId,
      });
      await queryClient.invalidateQueries({
        queryKey: ["housekeepingTypes", hotelId],
      });
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Failed to create item"));
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const updateItem = async (id: string, item: HousekeepingType) => {
    try {
      await updateHousekeepingType(callApi, id, {
        type: item.type,
        description: item.description,
        urgency: item.urgency,
      });
      await queryClient.invalidateQueries({
        queryKey: ["housekeepingTypes", hotelId],
      });
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Failed to update item"));
      throw err;
    }
  };

  const deleteItem = async (id: string) => {
    try {
      await deleteHousekeepingType(callApi, id);
      await queryClient.invalidateQueries({
        queryKey: ["housekeepingTypes", hotelId],
      });
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Failed to delete item"));
      throw err;
    }
  };

  return {
    items,
    isLoading,
    error,
    selectedItem,
    setSelectedItem,
    createItem,
    updateItem,
    deleteItem,
    pagination,
    setPage,
    setLimit,
    refresh: fetchItems,
    setSearchTerm,
  };
};
